import type { CmmdProject, CmmdWorkspace, RepositoryIdentity } from "@cmmd-forge/contracts";

import { deriveWorktreePathShapeKey } from "../../logicalProject";
import { deriveWorktreeLabel } from "../sidebar/worktreeLabel";

import { providerDisplayName, type SetupProjectRow } from "./firstAccountSetupData";

export interface FirstAccountSetupForgeProjectInput {
  readonly environmentId: string;
  readonly id: string;
  readonly name: string;
  readonly cwd: string;
  readonly availability?: SetupProjectRow["availability"] | null | undefined;
  readonly repositoryIdentity?:
    | (Pick<RepositoryIdentity, "name" | "owner" | "repositorySubpath"> & {
        readonly canonicalKey?: RepositoryIdentity["canonicalKey"];
      })
    | null
    | undefined;
  readonly defaultModelSelection?: {
    readonly provider?: string | null;
  } | null;
}

export interface FirstAccountSetupEnvironmentInput {
  readonly id: string;
  readonly label: string;
  readonly iconUrl?: string | null;
}

function workspaceKey(name: string | null | undefined): string {
  return name?.trim().toLowerCase() ?? "";
}

function cmmdWorkspaceForProject(
  project: CmmdProject,
  workspaces: ReadonlyMap<string, CmmdWorkspace>,
  workspacesByName: ReadonlyMap<string, CmmdWorkspace>,
): CmmdWorkspace | null {
  const workspaceId = project.workspaceId;
  if (workspaceId) {
    const byId = workspaces.get(workspaceId);
    if (byId) return byId;
  }

  const byName = workspacesByName.get(workspaceKey(project.workspaceName));
  return byName ?? null;
}

function environmentForProject(
  environments: ReadonlyMap<string, FirstAccountSetupEnvironmentInput>,
  environmentId: string,
): FirstAccountSetupEnvironmentInput {
  return environments.get(environmentId) ?? { id: environmentId, label: "Local" };
}

function splitPathSegments(path: string): readonly string[] {
  return path
    .replace(/\\/g, "/")
    .split("/")
    .filter((segment) => segment.length > 0);
}

function pathBasename(path: string): string {
  const segments = splitPathSegments(path);
  return segments[segments.length - 1] ?? "";
}

function pathFromSegments(segments: readonly string[]): string {
  if (segments.length === 0) return "";
  return `/${segments.join("/")}`;
}

function inferRepositorySubpathFromCwd(cwd: string): string | null {
  const segments = splitPathSegments(cwd);
  if (segments.length < 2) return null;

  const leaf = segments[segments.length - 1];
  const parent = segments[segments.length - 2];
  if (!leaf || !parent) return null;

  if (parent === "apps" || parent === "packages" || parent === "services") {
    return `${parent}/${leaf}`;
  }

  return null;
}

function findConductorIndex(segments: readonly string[]): number {
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    if (
      segments[i]?.toLowerCase() === "conductor" &&
      segments[i + 1]?.toLowerCase() === "workspaces"
    ) {
      return i;
    }
  }
  return -1;
}

function findClaudeIndex(segments: readonly string[]): number {
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    if (
      segments[i]?.toLowerCase() === ".claude" &&
      segments[i + 1]?.toLowerCase() === "worktrees"
    ) {
      return i;
    }
  }
  return -1;
}

function sourceNameFromCwd(cwd: string): string | null {
  const segments = splitPathSegments(cwd);
  const conductorIndex = findConductorIndex(segments);
  if (conductorIndex !== -1) return segments[conductorIndex + 2] ?? null;

  const claudeIndex = findClaudeIndex(segments);
  if (claudeIndex !== -1) return segments[claudeIndex - 1] ?? null;

  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const segment = segments[i];
    if (segment !== "worktrees" && segment !== ".worktrees") continue;
    const isCodexWorktree = i > 0 && segments[i - 1]?.toLowerCase() === ".codex";
    return isCodexWorktree ? (segments[i + 2] ?? null) : (segments[i + 1] ?? null);
  }

  const subpath = inferRepositorySubpathFromCwd(cwd);
  if (!subpath) return null;
  const skipCount = subpath.split("/").filter(Boolean).length;
  return segments[segments.length - 1 - skipCount] ?? null;
}

function sourceRootCwd(cwd: string): string {
  const segments = splitPathSegments(cwd);
  const conductorIndex = findConductorIndex(segments);
  if (conductorIndex !== -1) {
    return pathFromSegments(segments.slice(0, conductorIndex + 4));
  }

  const claudeIndex = findClaudeIndex(segments);
  if (claudeIndex !== -1) {
    return pathFromSegments(segments.slice(0, claudeIndex));
  }

  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const segment = segments[i];
    if (segment !== "worktrees" && segment !== ".worktrees") continue;
    const isCodexWorktree = i > 0 && segments[i - 1]?.toLowerCase() === ".codex";
    const rootEnd = isCodexWorktree ? i + 3 : i + 2;
    return pathFromSegments(segments.slice(0, rootEnd));
  }

  const subpath = inferRepositorySubpathFromCwd(cwd);
  if (!subpath) return cwd;
  const skipCount = subpath.split("/").filter(Boolean).length;
  return pathFromSegments(segments.slice(0, Math.max(segments.length - skipCount, 0)));
}

function preferredRepresentative(
  projects: readonly FirstAccountSetupForgeProjectInput[],
): FirstAccountSetupForgeProjectInput | undefined {
  const ranked = rankedForgeProjects(projects);
  return ranked.find((project) => sourceRootCwd(project.cwd) === project.cwd) ?? ranked[0];
}

function checkoutLabel(project: FirstAccountSetupForgeProjectInput): string | null {
  const repositorySubpath =
    project.repositoryIdentity?.repositorySubpath ?? inferRepositorySubpathFromCwd(project.cwd);
  const repositoryName = project.repositoryIdentity?.name?.trim() ?? sourceNameFromCwd(project.cwd);
  const skipSegments = new Set<string>();
  if (repositoryName) skipSegments.add(repositoryName);
  const repositoryOwner = project.repositoryIdentity?.owner?.trim();
  if (repositoryOwner) skipSegments.add(repositoryOwner);
  const label = deriveWorktreeLabel(
    project.cwd,
    repositorySubpath,
    skipSegments.size > 0 ? skipSegments : null,
  );
  const rawName = project.name.trim();
  if (label.primary && label.primary !== rawName) return label.primary;
  return null;
}

function forgeProjectName(project: FirstAccountSetupForgeProjectInput): string {
  const cwd = project.cwd.trim();
  const repositoryName = sourceNameFromCwd(cwd) ?? project.repositoryIdentity?.name?.trim();
  if (repositoryName) return repositoryName;

  const rawName = project.name.trim();
  if (!cwd) return rawName || "Untitled project";

  const basename = pathBasename(cwd);
  const isLeafName = rawName.length === 0 || rawName === basename;
  if (!isLeafName) return rawName;

  const repositorySubpath =
    project.repositoryIdentity?.repositorySubpath ?? inferRepositorySubpathFromCwd(cwd);
  const skipSegments = new Set<string>();
  const repositoryOwner = project.repositoryIdentity?.owner?.trim();
  if (repositoryOwner) skipSegments.add(repositoryOwner);

  const label = deriveWorktreeLabel(
    cwd,
    repositorySubpath,
    skipSegments.size > 0 ? skipSegments : null,
  );
  return label.primary || rawName || basename || "Untitled project";
}

function forgeProjectLogicalKey(project: FirstAccountSetupForgeProjectInput): string {
  const worktreeShape = deriveWorktreePathShapeKey(project.cwd);
  if (worktreeShape) return worktreeShape;

  const repositoryIdentity = project.repositoryIdentity;
  if (repositoryIdentity?.canonicalKey) {
    const subpath =
      repositoryIdentity.repositorySubpath?.trim() ?? inferRepositorySubpathFromCwd(project.cwd);
    return subpath
      ? `${repositoryIdentity.canonicalKey}#${subpath}`
      : repositoryIdentity.canonicalKey;
  }

  return `${project.environmentId}:${project.id}`;
}

function rankedForgeProjects(
  projects: readonly FirstAccountSetupForgeProjectInput[],
): readonly FirstAccountSetupForgeProjectInput[] {
  return projects.toSorted((a, b) => {
    const aAvailable = a.availability === "available" ? 0 : 1;
    const bAvailable = b.availability === "available" ? 0 : 1;
    return aAvailable - bAvailable;
  });
}

function checkoutSummary(projects: readonly FirstAccountSetupForgeProjectInput[]): string | null {
  if (projects.length <= 1) {
    const label = checkoutLabel(projects[0]!);
    return label ? `Checkout ${label}` : null;
  }
  const available = projects.filter((project) => project.availability === "available").length;
  const missing = projects.filter((project) => project.availability === "missing").length;
  const parts = [
    available > 0 ? `${available} available` : null,
    missing > 0 ? `${missing} missing` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? `${parts.join(" · ")} checkouts` : `${projects.length} checkouts`;
}

function groupedForgeProjects(projects: readonly FirstAccountSetupForgeProjectInput[]): readonly {
  readonly representative: FirstAccountSetupForgeProjectInput;
  readonly members: readonly FirstAccountSetupForgeProjectInput[];
}[] {
  const groups = new Map<string, FirstAccountSetupForgeProjectInput[]>();
  for (const project of projects) {
    const key = forgeProjectLogicalKey(project);
    const existing = groups.get(key);
    if (existing) existing.push(project);
    else groups.set(key, [project]);
  }

  return [...groups.values()].map((members) => {
    const representative = preferredRepresentative(members);
    return {
      representative: representative ?? members[0]!,
      members,
    };
  });
}

function cmmdProjectDetail(project: CmmdProject): string {
  if (project.brief?.trim()) return project.brief.trim();
  if (project.description?.trim()) return project.description.trim();
  if (project.workspaceName?.trim()) return project.workspaceName.trim();
  return "No repository linked yet.";
}

function cmmdProjectRepoUrl(project: CmmdProject): string | null {
  const directUrl = project.githubRepoUrl?.trim();
  if (directUrl) return directUrl;
  const projectLinks = project.projectLinks;
  if (!Array.isArray(projectLinks)) return null;
  for (const link of projectLinks) {
    if (typeof link !== "object" || link === null) continue;
    const url = (link as { readonly url?: unknown }).url;
    if (typeof url === "string" && url.includes("github.com/")) return url;
  }
  return null;
}

function cmmdProjectHasConnectedRepository(project: CmmdProject): boolean {
  return cmmdProjectRepoUrl(project) !== null;
}

export function buildFirstAccountSetupProjects(input: {
  readonly forgeProjects: ReadonlyArray<FirstAccountSetupForgeProjectInput>;
  readonly cmmdProjects: ReadonlyArray<CmmdProject>;
  readonly includeForgeProjects?: boolean;
  readonly cmmdWorkspaces?: ReadonlyArray<CmmdWorkspace>;
  readonly environments?: ReadonlyArray<FirstAccountSetupEnvironmentInput>;
}): readonly SetupProjectRow[] {
  const environments = new Map(
    (input.environments ?? []).map((environment) => [environment.id, environment]),
  );
  const cmmdWorkspaces = new Map(
    (input.cmmdWorkspaces ?? []).map((workspace) => [workspace.id, workspace]),
  );
  const cmmdWorkspacesByName = new Map(
    (input.cmmdWorkspaces ?? []).map((workspace) => [workspaceKey(workspace.name), workspace]),
  );
  return [
    ...(input.includeForgeProjects === false ? [] : groupedForgeProjects(input.forgeProjects)).map(
      ({ representative: project, members }) => {
        const environment = environmentForProject(environments, project.environmentId);
        const summary = checkoutSummary(members);
        return {
          id: `${project.environmentId}:${project.id}`,
          source: "forge" as const,
          setupAction: "open" as const,
          environmentId: project.environmentId,
          projectId: project.id,
          name: forgeProjectName(project),
          cwd: sourceRootCwd(project.cwd),
          availability: project.availability ?? "unknown",
          defaultProviderLabel: providerDisplayName(project.defaultModelSelection?.provider),
          environmentLabel: environment.label,
          environmentIconUrl: environment.iconUrl ?? null,
          projectIcon: null,
          projectIconUrl: null,
          githubRepoUrl: null,
          workspaceSummary: summary,
        };
      },
    ),
    ...input.cmmdProjects.filter(cmmdProjectHasConnectedRepository).map((project) => {
      const githubRepoUrl = cmmdProjectRepoUrl(project);
      const workspaceId = project.workspaceId ?? "cmmd";
      const workspace = cmmdWorkspaceForProject(project, cmmdWorkspaces, cmmdWorkspacesByName);
      return {
        id: `cmmd:${project.id}`,
        source: "cmmd" as const,
        setupAction: githubRepoUrl ? ("import" as const) : ("link-repo" as const),
        environmentId: `cmmd:${workspaceId}`,
        projectId: project.id,
        name: project.title,
        cwd: cmmdProjectDetail(project),
        availability: "unknown" as const,
        defaultProviderLabel: githubRepoUrl ? "Import from GitHub" : "Needs repo",
        environmentLabel: workspace?.name ?? project.workspaceName ?? "CMMD",
        environmentIconUrl:
          workspace?.iconUrl ?? workspace?.logoUrl ?? project.workspaceIconUrl ?? null,
        projectIcon: project.icon ?? null,
        projectIconUrl:
          project.iconUrl ??
          workspace?.iconUrl ??
          workspace?.logoUrl ??
          project.workspaceIconUrl ??
          null,
        githubRepoUrl,
      };
    }),
  ];
}
